// SettingsAccountDisplayPane.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Components
import Switch from 'components/switch/Switch';

/**
 * SettingsAccountDisplayPane
 *
 * Renders member-level display preferences for the active workspace.
 *
 * @param {object} theme - Active theme tokens for dynamic styling.
 * @param {boolean} isDarkMode - Effective dark mode state for current workspace.
 * @param {function} onThemeModeChange - Callback invoked when account dark mode toggle changes.
 * @param {boolean} isUsingWorkspaceDefault - Whether user has not set a personal override yet.
 *
 * @returns {ReactNode} The rendered account display settings pane.
 */
const SettingsAccountDisplayPane = React.memo(({
    theme,
    isDarkMode,
    onThemeModeChange,
    isUsingWorkspaceDefault,
}) => {

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- RENDER ----- //

    return (
        <>
            <div className="settings-modal-pane-header"
                style={{
                    padding: isMobile ? '6px' : '0px',
                }}>

                <div className="settings-modal-pane-header-left">
                    <div className="settings-modal-pane-title" style={{ color: theme.foreground }}>
                        Display
                    </div>
                    <div className="settings-modal-pane-subtitle" style={{ color: theme.foregroundMuted }}>
                        Personal appearance preferences
                    </div>
                </div>
            </div>

            <div className="settings-modal-pane-contents">
                <div className="settings-modal-theme-card">
                    <div className="settings-modal-theme-meta">
                        <div className="settings-modal-setting-label" style={{ color: theme.foreground }}>
                            Dark Mode
                        </div>
                        <div className="settings-modal-setting-description" style={{ color: theme.foregroundMuted }}>
                            {isUsingWorkspaceDefault
                                ? 'Currently using workspace default. Toggle to set your personal preference for this workspace.'
                                : 'Your personal preference for this workspace.'}
                        </div>
                    </div>

                    <Switch
                        checked={isDarkMode}
                        onChange={onThemeModeChange}
                    />
                </div>
            </div>
        </>
    );
});

// Dev Tools
SettingsAccountDisplayPane.displayName = 'SettingsAccountDisplayPane';

export default SettingsAccountDisplayPane;
